import { useState } from 'react'
import styled from 'styled-components'
import useTranslation from '../../hooks/useTranslation'

/*
 * CardOptions — a row of small cards to pick one from: the look of every card
 * picker in the app. It holds no data and fetches nothing; the caller hands it
 * the options and owns the selection.
 *
 *   CardSelect (./CardSelect.jsx)   an entity's records, from a config
 *   ExtractionPanel                 what a document suggests: supplier, titles,
 *                                   accounts, VAT codes — ranked, with odds
 *
 * Props:
 *   options     [{ id, title, subtitle?, badge? }], in the order to show them
 *   value       the selected option's id, or null
 *   onChange    (id | null) => void
 *   size        'small' | 'medium' | 'large' (default 'medium')
 *   count       how many cards show before "+N more" (default: all)
 *   wrap        wrap onto more rows instead of scrolling sideways
 *   allowClear  clicking the selected card clears it
 *   isRed       validation state — red border while nothing is chosen
 *   needed      a choice is still to be made — amber border while nothing
 *               is chosen, until a card turns green
 *   label       accessible name for the group (a translation key)
 *   testId      cards are `${testId}.option.${index}`
 *
 * One selected look, everywhere: the success green — its surface, a solid
 * border and a green title. (Until 2026-09-30 a selection was blue, or orange
 * when it was only a proposal, on a blue fill: three looks for one state, and
 * too faint to tell at a glance. How sure a suggestion is shows in its badge.)
 */

// The three sizes. A size is the whole visual scale of a card — its footprint
// and both font steps — so a caller picks one word rather than five numbers.
export const SIZES = {
    small:  { minWidth: '4.5rem', padding: '0.3rem 0.6rem',   gap: '0.375rem', title: 'var(--font-XS)', sub: 'var(--font-XXXS)' },
    medium: { minWidth: '6.5rem', padding: '0.5rem 0.85rem',  gap: '0.5rem',   title: 'var(--font-S)',  sub: 'var(--font-XXS)'  },
    large:  { minWidth: '8.5rem', padding: '0.75rem 1.15rem', gap: '0.625rem', title: 'var(--font-M)',  sub: 'var(--font-XS)'   },
}

export const Row = styled.div`
    display: flex;
    align-items: stretch;
    gap: ${({ $size }) => $size.gap};
    ${({ $wrap }) => $wrap ? 'flex-wrap: wrap;' : 'overflow-x: auto;'}
    padding-bottom: 0.125rem;
    scrollbar-width: thin;
`

const borderOf = ({ theme, $selected, $isRed, $needed }) =>
    $isRed ? theme.btn.danger
        : $selected ? theme.emotive.success.color
            : $needed ? theme.btn.yellow
                : theme.card.unselected

const Card = styled.button`
    flex: ${({ $wrap }) => $wrap ? '1 1 8rem' : '0 0 auto'};
    min-width: ${({ $size }) => $size.minWidth};
    max-width: 100%;
    padding: ${({ $size }) => $size.padding};
    text-align: left;
    cursor: pointer;
    border-radius: 0.5rem;
    transition: background-color 0.15s ease-in-out, border-color 0.15s ease-in-out;
    /* The border is always 2px so selecting a card never shifts the row. */
    border: 2px solid ${borderOf};
    background-color: ${({ theme, $selected }) => $selected ? theme.emotive.success.surface : theme.card.background};

    &:hover:not(:disabled) {
        background-color: ${({ theme, $selected }) => $selected ? theme.emotive.success.surface : theme.table.hover};
    }

    &:focus-visible {
        outline: 2px solid ${({ theme }) => theme.btn.primary};
        outline-offset: 2px;
    }
`

const CardHead = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.5rem;
`

const CardTitle = styled.div`
    font-size: ${({ $size }) => $size.title};
    font-weight: 700;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: ${({ theme, $selected }) => $selected ? theme.emotive.success.color : theme.font.color};
`

const CardSub = styled.div`
    font-size: ${({ $size }) => $size.sub};
    line-height: 1.2;
    margin-top: 0.15rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: ${({ $wrap }) => $wrap ? '100%' : '12rem'};
    color: ${({ theme }) => theme.font.label};
`

// The odds beside a suggestion — the emotive info tone, as a display's pills.
const Badge = styled.span`
    flex-shrink: 0;
    padding: 0 0.4rem;
    border-radius: 999px;
    font-size: ${({ $size }) => $size.sub};
    background-color: ${({ theme }) => theme.emotive.info.bg};
    color: ${({ theme }) => theme.emotive.info.color};
`

const More = styled.button`
    align-self: center;
    padding: 0;
    border: none;
    background: none;
    color: ${({ theme }) => theme.font.highlight};
    font: inherit;
    font-size: var(--font-XS);
    cursor: pointer;
`

const CardOptions = ({
    options = [], value = null, onChange, size = 'medium', count, wrap = false,
    allowClear = false, isRed = false, needed = false, label, testId
}) => {
    const { t, m } = useTranslation()
    const [all, setAll] = useState(false)
    const scale = SIZES[size] || SIZES.medium

    // The selected option always shows, even when it ranks past `count`.
    const head = count && !all ? options.slice(0, count) : options
    const selectedLater = value && !head.some(o => o.id === value) ? options.find(o => o.id === value) : null
    const shown = selectedLater ? [...head, selectedLater] : head
    const hidden = options.length - shown.length

    const pick = (id) => onChange?.(id === value && allowClear ? null : id)

    return (
        <Row $size={scale} $wrap={wrap} role='group' aria-label={label ? t(label) : undefined} data-testid={testId}>
            {shown.map((o, index) => {
                const selected = o.id === value
                return (
                    <Card key={o.id} type='button' $size={scale} $wrap={wrap}
                        $selected={selected} $isRed={isRed && !value} $needed={needed && !value}
                        aria-pressed={selected} title={o.subtitle ? `${o.title} — ${o.subtitle}` : o.title}
                        onClick={() => pick(o.id)}
                        data-testid={testId ? `${testId}.option.${index}` : undefined}>
                        <CardHead>
                            <CardTitle $size={scale} $selected={selected}>{o.title}</CardTitle>
                            {o.badge ? <Badge $size={scale}>{o.badge}</Badge> : null}
                        </CardHead>
                        {o.subtitle ? <CardSub $size={scale} $wrap={wrap}>{o.subtitle}</CardSub> : null}
                    </Card>
                )
            })}
            {hidden > 0 && <More type='button' onClick={() => setAll(true)}>{m('all.cardSelect.more', [hidden])}</More>}
        </Row>
    )
}

export default CardOptions
